import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Layout, Text} from '../..';
import {getTooltipPosition} from './functions/getTooltipPosition';
import {TooltipArrow} from './TooltipArrow';
import {TooltipProps} from './types';

const tooltipWidth = 140;
//TODO: calculate TooltipHeight
const tooltipHeight = 32;
export const Tooltip: React.FC<TooltipProps> = ({
  trigger,
  tooltipText,
  arrowPosition,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  //needed to calculate placement of tooltip relative to trigger
  const [triggerRect, setTriggerRect] = useState<
    undefined | Pick<DOMRect, 'x' | 'y' | 'width' | 'height'>
  >(undefined);

  // Ref for trigger and menu
  const triggerRef = useRef<HTMLElement>(null);

  // Handlers for opening/closing popup
  const showTooltip = useCallback(() => {
    setIsOpen(true);
  }, []);
  const closeTooltip = useCallback(() => setIsOpen(false), []);

  const position = useMemo((): {
    top: number | undefined;
    left: number | undefined;
  } => {
    return getTooltipPosition(triggerRect, arrowPosition, tooltipWidth);
  }, [arrowPosition, triggerRect]);

  // Renders the trigger component
  const renderTrigger = useMemo(() => {
    return (
      <span
        ref={triggerRef}
        onMouseOver={showTooltip}
        onMouseLeave={closeTooltip}
        style={{
          cursor: 'pointer',
        }}>
        {trigger}
      </span>
    );
  }, [closeTooltip, showTooltip, trigger]);

  // Renders the inner component
  const renderTooltip = useMemo(() => {
    if (isOpen && !loading) {
      return (
        <Layout
          position="fixed"
          zIndex={1000}
          left={position.left}
          top={position.top}>
          <Layout
            vertical={
              arrowPosition.startsWith('Top') ||
              arrowPosition.startsWith('Bottom')
            }>
            {(arrowPosition.startsWith('Top') ||
              arrowPosition.startsWith('Left')) && (
              <Layout vertical={arrowPosition.startsWith('Left')}>
                <TooltipArrow
                  tooltipWidth={tooltipWidth}
                  tooltipHeight={tooltipHeight}
                  arrowPosition={arrowPosition}
                />
              </Layout>
            )}
            <Layout
              raised
              backgroundColor="background_mono_inverse_primary"
              width={tooltipWidth}
              padding="14"
              borderRadius="8">
              <Text
                textColor="content_mono_inverse_primary"
                variant="paragraph_14_emphasis">
                {tooltipText}
              </Text>
            </Layout>
            {(arrowPosition.startsWith('Bottom') ||
              arrowPosition.startsWith('Right')) && (
              <Layout vertical={arrowPosition.startsWith('Right')}>
                <TooltipArrow
                  tooltipWidth={tooltipWidth}
                  tooltipHeight={tooltipHeight}
                  arrowPosition={arrowPosition}
                />
              </Layout>
            )}
          </Layout>
        </Layout>
      );
    }
  }, [
    arrowPosition,
    isOpen,
    loading,
    position.left,
    position.top,
    tooltipText,
  ]);

  // Handle measure when open changes
  useEffect(() => {
    if (isOpen) {
      const trigger = triggerRef.current;
      if (trigger) {
        const triggerRect = trigger.getBoundingClientRect();
        setLoading(false);
        setTriggerRect(triggerRect);
      }
    }
  }, [isOpen]);

  return (
    <>
      {renderTrigger}
      {renderTooltip}
    </>
  );
};
